// =============================================================================
// photoUploadService.ts — S3 upload + DealFile creation for NotesToHank photos
// =============================================================================

import { Storage, API } from 'aws-amplify';
import { createDealFile, updateDealFile } from '../../../graphql/mutations';
import { GamifyToast } from '../../common/CustomToasts';

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

const CLOUDFRONT_BASE = 'https://d22xeeaijfewl2.cloudfront.net/public';

// -----------------------------------------------------------------------------
// Exported Interfaces
// -----------------------------------------------------------------------------

export interface PhotoUploadResult {
  s3Key: string;
  cloudFrontUrl: string;
  dealFileId: string;
}

export interface PhotoUploadError {
  fileName: string;
  error: string;
  stage: 's3' | 'dealFile';
}

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

/**
 * Build the S3 key for a photo upload.
 * Pattern: deal-photos/{dealID}/{taskItemId}/{timestamp}-{filename}
 */
export function buildS3Key(
  dealId: string,
  taskItemId: string,
  fileName: string
): string {
  return `deal-photos/${dealId}/${taskItemId}/${Date.now()}-${fileName}`;
}

/**
 * Build the CloudFront URL for a given S3 key.
 */
export function buildCloudFrontUrl(s3Key: string): string {
  return `${CLOUDFRONT_BASE}/${s3Key}`;
}

// -----------------------------------------------------------------------------
// S3 Upload
// -----------------------------------------------------------------------------

/**
 * Upload a file to S3 at the given key.
 * Uses public access level and defaults content type to image/jpeg.
 */
export async function uploadToS3(s3Key: string, file: File): Promise<void> {
  await Storage.put(s3Key, file, {
    contentType: file.type || 'image/jpeg',
    level: 'public',
  });
}

// -----------------------------------------------------------------------------
// DealFile Record Creation
// -----------------------------------------------------------------------------

/**
 * Create a DealFile record via GraphQL.
 * On ConditionalCheckFailedException, retries with updateDealFile.
 */
/* eslint-disable camelcase */
export async function createDealFileRecord(
  s3Key: string,
  dealId: string,
  taskItemId: string,
  orgId: string,
  userId: string,
  mimeType: string,
  sequence: number
): Promise<void> {
  const input = {
    id: s3Key,
    orgID: orgId,
    dealID: dealId,
    deal_id: dealId,
    task_id: taskItemId,
    type: mimeType,
    url: s3Key,
    tags: [],
    description: '',
    sequence,
    createdBy: userId,
    lastUpdatedBy: userId,
    isDeleted: false,
    fileTimestampEpoch: Date.now(),
  };

  try {
    await API.graphql({ query: createDealFile, variables: { input } });
  } catch (err: any) {
    // Requirement 2.4: Retry with updateDealFile on ConditionalCheckFailedException
    const errorType = err?.errors?.[0]?.errorType || '';
    if (errorType.includes('ConditionalCheckFailedException')) {
      const {
        id,
        orgID,
        dealID,
        deal_id,
        task_id,
        type,
        url,
        tags,
        sequence: seq,
        lastUpdatedBy,
        isDeleted,
        fileTimestampEpoch,
      } = input;
      await API.graphql({
        query: updateDealFile,
        variables: {
          input: {
            id,
            orgID,
            dealID,
            deal_id,
            task_id,
            type,
            url,
            tags,
            sequence: seq,
            lastUpdatedBy,
            isDeleted,
            fileTimestampEpoch,
          },
        },
      });
    } else {
      throw err;
    }
  }
}
/* eslint-enable camelcase */

// -----------------------------------------------------------------------------
// Single Photo Upload
// -----------------------------------------------------------------------------

/**
 * Upload a single photo to S3 and create the corresponding DealFile record.
 * Returns the S3 key, CloudFront URL, and DealFile ID on success.
 */
export async function uploadPhoto(
  file: File,
  dealId: string,
  taskItemId: string,
  orgId: string,
  userId: string,
  sequence: number
): Promise<PhotoUploadResult> {
  const s3Key = buildS3Key(dealId, taskItemId, file.name);

  // Step 1: Upload to S3
  try {
    await uploadToS3(s3Key, file);
  } catch (err: any) {
    GamifyToast.error('Upload Failed', `Failed to upload ${file.name}`);
    throw err;
  }

  // Step 2: Create DealFile record
  try {
    await createDealFileRecord(
      s3Key,
      dealId,
      taskItemId,
      orgId,
      userId,
      file.type || 'image/jpeg',
      sequence
    );
  } catch (err: any) {
    GamifyToast.error(
      'Record Creation Failed',
      `Photo uploaded but failed to create record for ${file.name}`
    );
    throw err;
  }

  return {
    s3Key,
    cloudFrontUrl: buildCloudFrontUrl(s3Key),
    dealFileId: s3Key,
  };
}

// -----------------------------------------------------------------------------
// Batch Photo Upload
// -----------------------------------------------------------------------------

/**
 * Upload multiple photos for a single task item.
 * Each file is uploaded independently with a 1-based sequence number.
 * Returns arrays of succeeded and failed results.
 */
export async function uploadPhotoBatch(
  files: File[],
  dealId: string,
  taskItemId: string,
  orgId: string,
  userId: string
): Promise<{ succeeded: PhotoUploadResult[]; failed: PhotoUploadError[] }> {
  const succeeded: PhotoUploadResult[] = [];
  const failed: PhotoUploadError[] = [];

  for (let i = 0; i < files.length; i++) {
    try {
      const result = await uploadPhoto(
        files[i],
        dealId,
        taskItemId,
        orgId,
        userId,
        i + 1
      );
      succeeded.push(result);
    } catch (err: any) {
      const errorMessage = err?.message || 'Upload failed';
      // Determine which stage failed based on whether S3 upload succeeded
      // If the error came from createDealFileRecord, the S3 upload already passed
      const stage: 's3' | 'dealFile' = errorMessage.includes('record')
        ? 'dealFile'
        : 's3';
      failed.push({
        fileName: files[i].name,
        error: errorMessage,
        stage,
      });
    }
  }

  return { succeeded, failed };
}
